import { formatDistanceToNow } from 'date-fns'

import { CAREFORCE_ORG_SLUG } from '@/config/constants'
import { ApiError } from '@/lib/api'
import type { Snippet, SnippetOrgUsage, SnippetVariant } from './types'

/**
 * Collapses a snippet body to one prose line for a picker row: heading
 * markers, `*` emphasis and backticks go, `{tokens}` stay. Underscore
 * emphasis is left alone — `_` is part of most token names.
 */
export function contentPreview(content: string): string {
  return (
    content
      .replace(/^ {0,3}#{1,6}\s+/gm, '')
      .replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1')
      .replace(/`+/g, '')
      .replace(/\s+/g, ' ')
      .trim() || 'Empty snippet'
  )
}

/** "3 days ago" — the prose form. Empty for a date the API sent malformed. */
export function relativeUpdatedAt(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? ''
    : formatDistanceToNow(date, { addSuffix: true })
}

/** A Careforce row this org reads but does not own. */
export function isCareforceSnippet(snippet: Snippet): boolean {
  return snippet.read_only === true
}

export function canCustomize(snippet: Snippet): boolean {
  return isCareforceSnippet(snippet) && !snippet.is_hidden
}

/** This org's own copy of a shared snippet whose source is still live. */
export function isCopyOfShared(snippet: Snippet): boolean {
  return Boolean(snippet.source_snippet_id) && !isCareforceSnippet(snippet)
}

/**
 * Careforce changed the text after this org diverged. Compares the two content
 * clocks, so a rename or a share toggle on either side never trips it.
 */
export function isOutOfDate(snippet: Snippet): boolean {
  if (!isCopyOfShared(snippet)) return false
  const ours = snippet.content_updated_at
  const theirs = snippet.source_content_updated_at
  if (!ours || !theirs) return false
  return Date.parse(theirs) > Date.parse(ours)
}

/** "1 duty", "2 duties", "3 orgs". */
export const plural = (count: number, noun: string) =>
  `${count} ${count === 1 ? noun : `${noun.replace(/y$/, 'ie')}s`}`

export type SharedFilter = 'all' | 'shared' | 'private'

/** The Careforce library's filter chips. */
export function matchesSharedFilter(
  snippet: Snippet,
  filter: SharedFilter
): boolean {
  return (
    filter === 'all' || (filter === 'shared') === Boolean(snippet.is_shared)
  )
}

/** Careforce rows apart from the org's own, in list order. */
export function partitionSnippets<T extends Snippet>(
  snippets: readonly T[]
): { careforce: T[]; own: T[] } {
  const careforce: T[] = []
  const own: T[] = []
  for (const snippet of snippets) {
    ;(isCareforceSnippet(snippet) ? careforce : own).push(snippet)
  }
  return { careforce, own }
}

/** The secondary line on a list row. Never names the org: "your duties". */
export function snippetMeta(snippet: Snippet): string {
  const count = snippet.instance_count ?? 0
  const updated = relativeUpdatedAt(snippet.updated_at)

  if (isCareforceSnippet(snippet)) {
    const parts = [
      count === 0
        ? 'Not used by your duties yet'
        : `Used by ${count} of your duties`,
    ]
    if (updated) parts.push(`Updated by Careforce ${updated}`)
    return parts.join(' • ')
  }

  const variants = snippet.override_count ?? 0
  const parts = [plural(count, 'duty')]
  if (variants > 0) parts.push(plural(variants, 'variant'))

  if (snippet.is_shared && snippet.other_org_count != null) {
    const orgs = snippet.other_org_count
    if (orgs > 0) {
      parts.push(
        `${plural(orgs, 'org')}, ${snippet.customized_org_count ?? 0} customized`
      )
    }
    if (updated) parts.push(`Updated ${updated}`)
    return parts.join(' • ')
  }

  if (isCopyOfShared(snippet)) {
    const theirs = relativeUpdatedAt(snippet.source_content_updated_at ?? '')
    if (theirs) parts.push(`Careforce updated theirs ${theirs}`)
    if (updated) parts.push(`Yours updated ${updated}`)
    return parts.join(' • ')
  }

  parts.push(`${snippet.content.length.toLocaleString()} characters`)
  if (updated) parts.push(`Updated ${updated}`)
  return parts.join(' • ')
}

export function snippetErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

/** A lost update: someone saved between our load and our write. */
export function isStaleWriteError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 412
}

/**
 * The name column is unique per org, so the API answers 409 on a collision.
 * Told apart by code, not wording: the same route answers 409 for "stop
 * sharing" refused and for a copy that already exists.
 */
export function isDuplicateNameError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'duplicate_name'
}

/** The org already holds a live copy of this shared snippet: open that one. */
export function isCopyExistsError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'copy_exists'
}

/** Decoration from the design, the same for every snippet. It identifies
 *  nothing on its own. */
const VARIANT_ID_PREFIX = 'IN-V'

/**
 * The display id beside a variant row: `IN-V01`, `IN-V02`, …
 *
 * Display only — the API keys a variant by its duty, and there is a unique
 * constraint on (snippet, duty), so this number is a position in the list and
 * never an identifier to send back.
 */
export function variantDisplayId(index: number): string {
  return `${VARIANT_ID_PREFIX}${String(index + 1).padStart(2, '0')}`
}

/** The one row whose diff is open beside the Variants or Organizations tab. */
export type VariantSelection =
  | { kind: 'duty'; dutyId: string }
  | { kind: 'orgCopy'; orgSlug: string }
  | { kind: 'orgVariant'; orgSlug: string; dutyId: string }
export type DutyRow = Extract<VariantSelection, { kind: 'duty' }>
export type OrgRow = Exclude<VariantSelection, DutyRow>

export function selectionKey(selection: VariantSelection): string {
  if (selection.kind === 'duty') return `duty:${selection.dutyId}`
  if (selection.kind === 'orgCopy') return `orgCopy:${selection.orgSlug}`
  return `orgVariant:${selection.orgSlug}:${selection.dutyId}`
}

export function orgDisplayName(org: SnippetOrgUsage): string {
  return org.org_name ?? org.org_slug
}

const orgRank = (org: SnippetOrgUsage) =>
  !org.copy ? 2 : org.copy.out_of_date ? 0 : 1

/** The Organizations tab rows: customized and out of date first, then
 *  customized, then the rest. */
export function otherOrgs(usage: SnippetOrgUsage[]): SnippetOrgUsage[] {
  return usage
    .filter((org) => org.org_slug !== CAREFORCE_ORG_SLUG)
    .toSorted(
      (a, b) =>
        orgRank(a) - orgRank(b) ||
        orgDisplayName(a).localeCompare(orgDisplayName(b))
    )
}

export interface OpenDiff {
  /** Names the old side; `variant.duty_name` names the new one. */
  from: string
  sharedContent: string
  variant: SnippetVariant
  /** An org's rows: the org that manages the new side. */
  orgName?: string
  outOfDate?: boolean
}

/**
 * What the diff pane compares for the open row: a duty's variant against the
 * saved text, an org's customization against Careforce's saved text, or one of
 * that org's variants against its customization.
 */
export function openDiffFor(
  selection: VariantSelection | null,
  {
    savedContent,
    variants,
    usage,
    showsOrgs,
  }: {
    savedContent: string
    variants: SnippetVariant[] | null
    usage: SnippetOrgUsage[] | null
    /** Careforce staff on a shared row: both sides are named. */
    showsOrgs: boolean
  }
): OpenDiff | null {
  if (selection === null) return null
  if (selection.kind === 'duty') {
    const variant = variants?.find((row) => row.duty_id === selection.dutyId)
    if (!variant) return null
    return {
      from: showsOrgs ? 'Careforce' : 'Shared',
      sharedContent: savedContent,
      variant,
    }
  }
  if (!showsOrgs) return null
  const org = usage?.find((row) => row.org_slug === selection.orgSlug)
  const copy = org?.copy
  if (!org || !copy) return null
  const orgName = orgDisplayName(org)
  if (selection.kind === 'orgCopy') {
    return {
      from: 'Careforce',
      sharedContent: savedContent,
      orgName,
      outOfDate: copy.out_of_date,
      variant: {
        duty_id: copy.id,
        duty_name: orgName,
        content: copy.content,
        created_at: copy.updated_at,
        updated_at: copy.updated_at,
      },
    }
  }
  const variant = copy.variants.find((row) => row.duty_id === selection.dutyId)
  if (!variant) return null
  return { from: orgName, sharedContent: copy.content, orgName, variant }
}
